import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function PromotionsPage() {
  const pageTitle = "Spin & Win - Promotions";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [promotions, setPromotions] = useState([]);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [promotionTitle, setPromotionTitle] = useState(null);
  const [promotionDescription, setPromotionDescription] = useState("");
  const [promotionExpiry, setPromotionExpiry] = useState("");
  const [promotionBanner, setPromotionBanner] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  const tableColumns = [
    { Header: "Title", accessor: "title", align: "left" },
    { Header: "Expires On", accessor: "expires_on", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Created", accessor: "created", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        title: (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {row.title}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.description || "---"}
            </MDTypography>
          </MDBox>
        ),

        expires_on: row.expires_on || "---",

        status: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color={row.status ? "success" : "text"}
          >
            {row.status ? "Active" : "Inactive"}
          </MDTypography>
        ),

        created: row.created || "---",

        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={1}>
            <Tooltip title="Manage Promotion">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/tm/spin/promotions/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  settings
                </Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title={row.status ? "Deactivate" : "Activate"}>
              <MDButton
                size="small"
                variant="outlined"
                color={row.status ? "warning" : "success"}
                iconOnly
                onClick={() => {
                  updatePromotionStatus(row.reference);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  {row.status ? "pause" : "play_arrow"}
                </Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title="Delete Promotion">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  setSelectedPromotion(row);
                  setOpenDeleteDialog(true);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get("/portal/tm/spin/promotions.json")
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.promotions);
            setPromotions(rows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  const resetPromotionForm = () => {
    setPromotionTitle(null);
    setPromotionDescription("");
    setPromotionExpiry("");
    setPromotionBanner(null);
  };

  async function createPromotion() {
    if (promotionTitle === null || promotionTitle.trim() === "") {
      setSeverity("error");
      setErrMsg("Enter promotion title.");
      setOpen(true);
      return;
    }

    if (!promotionExpiry) {
      setSeverity("error");
      setErrMsg("Select promotion expiry date.");
      setOpen(true);
      return;
    }

    setOpenCreateDialog(false);
    setShowLoading(true);

    const formData = new FormData();
    formData.append("title", promotionTitle.trim());
    formData.append("description", promotionDescription);

    const expiryTimestamp = Math.floor(new Date(`${promotionExpiry}T23:59:59`).getTime() / 1000);
    formData.append("expires_on", String(expiryTimestamp));

    if (promotionBanner) {
      formData.append("banner", promotionBanner);
    }

    await axios
      .post("portal/tm/spin/promotions/create.json", formData)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          resetPromotionForm();

          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          await statusDialog.show(result.data.data.message, "success", "Success!");

          // navigate(`/tm/spin/promotions/${result.data.data.reference}`);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updatePromotionStatus(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/tm/spin/promotions/${reference}/status.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deletePromotion() {
    if (!selectedPromotion) {
      return;
    }

    setOpenDeleteDialog(false);
    setShowLoading(true);

    await axios
      .get(`/portal/tm/spin/promotions/${selectedPromotion.reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setSelectedPromotion(null);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={openCreateDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenCreateDialog(false)}
      >
        <DialogTitle>
          <MDBox>Create Spin & Win Promotion</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Promotion Title"
                type="text"
                fullWidth
                variant="outlined"
                value={promotionTitle || ""}
                onChange={(e) => setPromotionTitle(e.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={promotionDescription}
                onChange={(e) => setPromotionDescription(e.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="expires_on"
                margin="dense"
                label="Expiry Date"
                type="date"
                fullWidth
                variant="outlined"
                value={promotionExpiry}
                onChange={(e) => setPromotionExpiry(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>

            <MDBox mt={3}>
              <MDTypography variant="button" fontWeight="medium">
                Promotion Banner
              </MDTypography>

              <MDBox mt={1}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setPromotionBanner(
                      e.target.files && e.target.files.length ? e.target.files[0] : null
                    );
                  }}
                />
              </MDBox>

              {promotionBanner && (
                <MDTypography variant="caption" color="text">
                  {promotionBanner.name}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateDialog(false);
              resetPromotionForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createPromotion();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        fullWidth
        maxWidth="xs"
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>
          <MDBox>Delete Promotion</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete <strong>{selectedPromotion?.title}</strong>?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenDeleteDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton
            color="error"
            variant="contained"
            onClick={() => {
              deletePromotion();
            }}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={1}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>casino</Icon>
                      </MDTypography>

                      <MDTypography variant="h6" fontWeight="bold" fontSize={16}>
                        Spin & Win Promotions
                      </MDTypography>

                      <MDTypography mt={1} variant="h6" fontWeight="light" fontSize={16}>
                        Setup and manage your Spin & Win promotions seamlessly.
                        <br />
                        Configure wheels, segments, prizes and participants.
                      </MDTypography>
                    </MDBox>

                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          resetPromotionForm();
                          setOpenCreateDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Promotion
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>

            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Promotions
                        </MDTypography>
                      </MDBox>

                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            resetPromotionForm();
                            setOpenCreateDialog(true);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <MDBox p={0}>
                    <DataTable
                      table={{ columns: tableColumns, rows: promotions }}
                      canSearch={false}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default PromotionsPage;
